/**
 * Cart Drawer — B variant (A/B test CART_001, De Lampfabriek)
 * - Shipping insurance toggle + variant selection + add/remove before checkout
 * - Complementary products SwiperJS slider + quick add-to-cart
 * - Cart note expand/collapse
 * Only active markup lives behind the `et-cart-b` body reveal class, but the
 * custom elements register unconditionally (they no-op when B isn't shown).
 */
(function () {
  'use strict';

  var SWIPER_JS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
  var SWIPER_CSS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
  var swiperPromise = null;

  function loadSwiper() {
    if (window.Swiper) return Promise.resolve(window.Swiper);
    if (swiperPromise) return swiperPromise;
    swiperPromise = new Promise(function (resolve, reject) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = SWIPER_CSS;
      document.head.appendChild(link);
      var script = document.createElement('script');
      script.src = SWIPER_JS;
      script.onload = function () { resolve(window.Swiper); };
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return swiperPromise;
  }

  function money(cents, format) {
    if (typeof window.formatMoney === 'function') {
      try { return window.formatMoney(cents, format); } catch (e) { /* fall through */ }
    }
    return (cents / 100).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
  }

  /* ------------------------------------------------------------------ */
  /* Footer controller: note, insurance toggle, totals, checkout        */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('cart-drawer-b-controls')) {
    customElements.define('cart-drawer-b-controls', class extends HTMLElement {
      connectedCallback() {
        this.moneyFormat = this.dataset.moneyFormat || '';
        this.itemsFinal = parseInt(this.dataset.itemsFinal, 10) || 0;
        this.itemsOriginal = parseInt(this.dataset.itemsOriginal, 10) || 0;
        this.discountLabel = this.dataset.discountLabel || '';
        this.fsThreshold = parseInt(this.dataset.fsThreshold, 10) || 0;
        this.shippingCost = parseInt(this.dataset.shippingCost, 10) || 0;
        this.shippingFreeLabel = this.dataset.shippingFree || '';
        this.drawerRoot = this.closest('.cart-drawer__b') || document;

        this.parseInsurance();
        this.setupNote();
        this.setupInsurance();
        this.setupCheckout();
      }

      parseInsurance() {
        this.insData = null;
        var tag = this.querySelector('.cd-b-ins-data');
        if (!tag) return;
        try { this.insData = JSON.parse(tag.textContent); } catch (e) { this.insData = null; }
      }

      chosenInsVariant() {
        if (!this.insData) return null;
        var list = this.insData.variants || [];
        var found = list.filter(function (v) { return v.id === this.insData.chosenId; }, this)[0];
        return found || list[0] || null;
      }

      /* --- 5. Cart note --- */
      setupNote() {
        var note = this.querySelector('.cd-b__note');
        if (!note) return;
        var trigger = note.querySelector('.cd-b__note-trigger');
        var save = note.querySelector('.cd-b__note-save');
        var input = note.querySelector('.cd-b__note-input');
        if (trigger) {
          trigger.addEventListener('click', function () { note.classList.toggle('is-open'); });
        }
        if (save && input) {
          save.addEventListener('click', function () {
            fetch((window.theme && theme.routes ? theme.routes.cart_update_url : '/cart/update') + '.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ note: input.value })
            });
            note.classList.remove('is-open');
          });
        }
      }

      /* --- 6. Insurance toggle --- */
      setupInsurance() {
        this.switch = this.querySelector('.cd-b__switch');
        // Persisted state survives drawer re-renders; defaults to the setting.
        var stored = sessionStorage.getItem('cd_b_insurance');
        this.insuranceOn = stored === null ? (this.dataset.defaultOn === 'true') : (stored === '1');
        this.applyInsuranceState();

        if (this.switch) {
          this.switch.addEventListener('click', function () {
            this.insuranceOn = !this.insuranceOn;
            sessionStorage.setItem('cd_b_insurance', this.insuranceOn ? '1' : '0');
            this.applyInsuranceState();
          }.bind(this));
        }
      }

      applyInsuranceState() {
        if (this.switch) this.switch.setAttribute('aria-checked', this.insuranceOn ? 'true' : 'false');
        var row = this.querySelector('.cd-b__row-insurance');
        if (row) row.hidden = !this.insuranceOn;
        this.updateTotals();
        this.updateFreeShipping();
      }

      insurancePrice() {
        var v = (this.insuranceOn) ? this.chosenInsVariant() : null;
        return v ? v.price : 0;
      }

      /* Free shipping bar + shipping line — insurance counts toward the goal */
      updateFreeShipping() {
        if (!this.fsThreshold) return;
        var subtotal = this.itemsFinal + this.insurancePrice();
        var remaining = this.fsThreshold - subtotal;
        var reached = remaining <= 0;

        var fs = this.drawerRoot.querySelector('.cd-b__fs');
        if (fs) {
          fs.classList.toggle('is-reached', reached);
          if (!reached) {
            var pct = Math.min(100, Math.round(subtotal / this.fsThreshold * 100));
            fs.style.setProperty('--cd-fs-pct', pct + '%');
            var amountEl = fs.querySelector('.cd-b__fs-amount');
            if (amountEl) amountEl.textContent = money(remaining, this.moneyFormat);
          }
        }

        // Keep the shipping total line in sync with the goal state.
        var shipEl = this.querySelector('.cd-b__shipping-val');
        if (shipEl) {
          shipEl.textContent = reached ? this.shippingFreeLabel : money(this.shippingCost, this.moneyFormat);
        }
      }

      /* --- 7. Totals --- */
      updateTotals() {
        var v = this.chosenInsVariant();
        var insPrice = (this.insuranceOn && v) ? v.price : 0;
        var insCompare = (this.insuranceOn && v) ? (v.compare > v.price ? v.compare : v.price) : 0;

        var finalTotal = this.itemsFinal + insPrice;
        var originalTotal = this.itemsOriginal + insCompare;

        var finalEl = this.querySelector('.cd-b__total-final');
        var origEl = this.querySelector('.cd-b__total-original');
        var pillEl = this.querySelector('.cd-b__discount-pill');

        if (finalEl) finalEl.textContent = money(finalTotal, this.moneyFormat);

        var hasDiscount = originalTotal > finalTotal;
        if (origEl) {
          origEl.textContent = money(originalTotal, this.moneyFormat);
          origEl.hidden = !hasDiscount;
        }
        if (pillEl) {
          if (hasDiscount) {
            var pct = Math.round((originalTotal - finalTotal) / originalTotal * 100);
            pillEl.textContent = pct + '% ' + this.discountLabel;
            pillEl.hidden = false;
          } else {
            pillEl.hidden = true;
          }
        }
      }

      /* --- 8. Checkout: add/remove insurance, then redirect --- */
      chooseVariant(subCents, variants) {
        var subMajor = subCents / 100;
        var chosen = null, chosenFloor = -Infinity;
        variants.forEach(function (variant) {
          var floor = parseFloat(String(variant.title).split('T')[0]);
          if (!isNaN(floor) && floor <= subMajor && floor >= chosenFloor) {
            chosen = variant;
            chosenFloor = floor;
          }
        });
        return chosen || variants[0] || null;
      }

      setupCheckout() {
        var btn = this.querySelector('[data-cd-checkout]');
        if (!btn) return;
        btn.addEventListener('click', this.onCheckout.bind(this));
      }

      onCheckout(e) {
        var btn = e.currentTarget;
        var url = this.dataset.checkoutUrl || '/checkout';
        // No insurance product configured → let the form submit normally.
        if (!this.insData) return;

        e.preventDefault();
        btn.classList.add('loading');

        var self = this;
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var insVariantIds = self.insData.variants.map(function (v) { return v.id; });
            var subtotal = 0;
            var presentIns = [];
            cart.items.forEach(function (item) {
              if (item.product_id === self.insData.productId) {
                presentIns.push(item);
              } else {
                subtotal += (typeof item.final_line_price === 'number') ? item.final_line_price : item.line_price;
              }
            });

            var updates = {};
            var chain = Promise.resolve();

            if (self.insuranceOn) {
              var chosen = self.chooseVariant(subtotal, self.insData.variants);
              // Remove any insurance variant that isn't the chosen one.
              presentIns.forEach(function (line) {
                if (!chosen || line.variant_id !== chosen.id) updates[line.variant_id] = 0;
              });
              if (Object.keys(updates).length) {
                chain = chain.then(function () { return self.cartUpdate(updates); });
              }
              // Add the chosen variant if it isn't already in the cart.
              var already = presentIns.some(function (l) { return chosen && l.variant_id === chosen.id; });
              if (chosen && !already) {
                chain = chain.then(function () { return self.cartAdd(chosen.id); });
              }
            } else {
              // Insurance OFF → remove every insurance line.
              presentIns.forEach(function (line) { updates[line.variant_id] = 0; });
              if (Object.keys(updates).length) {
                chain = chain.then(function () { return self.cartUpdate(updates); });
              }
            }

            return chain;
          })
          .catch(function (err) { console.error('[cart-b] checkout insurance error', err); })
          .finally(function () { window.location.href = url; });
      }

      cartUpdate(updates) {
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates: updates })
        });
      }

      cartAdd(id) {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: id, quantity: 1 })
        });
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Complementary products: fetch, Swiper slider, quick add            */
  /* ------------------------------------------------------------------ */
  if (!customElements.get('cd-b-recommendations')) {
    customElements.define('cd-b-recommendations', class extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', this.onClick.bind(this));
        if (this.querySelector('.swiper')) {
          this.initSwiper();
        } else if (this.dataset.url) {
          this.fetchProducts();
        }
      }

      fetchProducts() {
        var self = this;
        fetch(this.dataset.url)
          .then(function (r) { return r.text(); })
          .then(function (text) {
            var doc = new DOMParser().parseFromString(text, 'text/html');
            var source = doc.querySelector('cd-b-recommendations');
            if (source && source.innerHTML.trim().length) {
              self.innerHTML = source.innerHTML;
              self.initSwiper();
            } else {
              var wrap = self.closest('.cd-b__rec');
              if (wrap) wrap.hidden = true;
            }
          })
          .catch(function (e) { console.error('[cart-b] recommendations error', e); });
      }

      initSwiper() {
        var container = this.querySelector('.swiper');
        if (!container || container.classList.contains('swiper-initialized')) return;
        var pagination = this.querySelector('.swiper-pagination');
        loadSwiper().then(function (Swiper) {
          if (!Swiper) return;
          new Swiper(container, {
            slidesPerView: 1,
            spaceBetween: 8,
            watchOverflow: true,
            pagination: pagination ? { el: pagination, clickable: true } : false,
            breakpoints: {
              768: {
                slidesPerView: 1.3,
              },
            }
          });
        });
      }

      onClick(e) {
        var btn = e.target.closest('.cd-b__rec-add');
        if (!btn) return;
        e.preventDefault();
        if (btn.classList.contains('loading')) return;
        btn.classList.add('loading');
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: btn.dataset.variantId, quantity: 1 })
        })
          .then(function (r) { return r.json(); })
          .then(function () {
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          })
          .catch(function (e) { console.error('[cart-b] add error', e); })
          .finally(function () { btn.classList.remove('loading'); });
      }
    });
  }
})();
